import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import Navbar from './components/Navbar';
import AnnouncementBanner from './components/AnnouncementBanner';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import { initializeConsoleSuppression } from './utils/consoleSuppression';

import { PageSkeleton } from './components/LoadingState';

// Robust lazy loader with one-time auto-reload on chunk load errors during HMR or stale caches
const lazyWithRetry = (importer) => {
  let hasRetried = false;
  return React.lazy(() =>
    importer().catch((error) => {
      const message = (error && error.message) || '';
      const isChunkError = /Loading chunk|ChunkLoadError|Unexpected token <|failed to fetch/i.test(message);
      if (isChunkError && !hasRetried) {
        hasRetried = true;
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('v', String(Date.now()));
          window.location.replace(url.toString());
          return new Promise(() => {}); // keep pending until reload
        } catch (_e) {
          window.location.reload();
          return new Promise(() => {});
        }
      }
      throw error;
    })
  );
};

// Lazy load page components with retry for better resilience
const Home = lazyWithRetry(() => import('./pages/Home'));
const Fleet = lazyWithRetry(() => import('./pages/Fleet'));
const DriverApplication = lazyWithRetry(() => import('./pages/DriverApplication'));
const ApplicationSuccess = lazyWithRetry(() => import('./pages/ApplicationSuccess'));
const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazyWithRetry(() => import('./pages/TermsOfService'));
const Safety = lazyWithRetry(() => import('./pages/Safety'));
const Redesign = lazyWithRetry(() => import('./pages/Redesign'));
const CdlJobs = lazyWithRetry(() => import('./pages/CdlJobs'));
const LocalCdlJobs = lazyWithRetry(() => import('./pages/LocalCdlJobs'));
const About = lazyWithRetry(() => import('./pages/About'));

// Enhanced loading component
const PageLoader = ({ error = false }) => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      gap: 3
    }}
  >
    {error ? (
      <>
        <Typography variant="h4" color="error">
          Failed to load page
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Please refresh the page or try again later
        </Typography>
      </>
    ) : (
      <>
        <CircularProgress size={48} />
        <Typography variant="h4" color="text.secondary">
          Loading...
        </Typography>
        <PageSkeleton />
      </>
    )}
  </Box>
);

// Error boundary for lazy loaded components
class LazyErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Lazy loading error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <PageLoader error={true} />;
    }

    return this.props.children;
  }
}

function AppShell() {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const hideFooter = isMobile && location.pathname.startsWith('/apply');

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      backgroundColor: 'background.default' 
    }}>
      <ScrollToTop />
      <AnnouncementBanner />
      <Navbar />
      <Box component="main" sx={{ flexGrow: 1 }}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<About />} />
            <Route path="/fleet" element={<Fleet />} />
            <Route path="/apply" element={<DriverApplication />} />
            <Route path="/cdl-jobs" element={<CdlJobs />} />
            <Route path="/cdl-jobs/:citySlug" element={<LocalCdlJobs />} />
            <Route path="/drive-for-us" element={<Navigate to="/cdl-jobs" replace />} />
            <Route path="/application-success" element={<ApplicationSuccess />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/safety" element={<Safety />} />
            <Route path="/about/redesign-2025" element={<Redesign />} />
          </Routes>
        </Suspense>
      </Box>
      {!hideFooter && <Footer />}
    </Box>
  );
}

function App() {
  // Initialize console suppression for development
  React.useEffect(() => {
    initializeConsoleSuppression();
  }, []);

  return (
    <LazyErrorBoundary>
      <Router>
        <AppShell />
      </Router>
    </LazyErrorBoundary>
  );
}

export default App; 